/**
 * Team Edit Dialog
 * Dialog for managing team members and peer reviewers
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

interface TeamEditDialogProps {
  open: boolean;
  project: any;
  onClose: () => void;
  onSave: (teamData: any) => Promise<void>;
}

export const TeamEditDialog: React.FC<TeamEditDialogProps> = ({
  open,
  project,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    memberNames: [] as string[],
    peerReviewerNames: [] as string[]
  });
  const [newMemberName, setNewMemberName] = useState('');
  const [newReviewerName, setNewReviewerName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project?.team) {
      setFormData({
        memberNames: project.team.memberNames || [],
        peerReviewerNames: project.team.peerReviewerNames || []
      });
    }
  }, [project]);

  const handleAddMember = () => {
    if (newMemberName.trim()) {
      setFormData(prev => ({
        ...prev,
        memberNames: [...prev.memberNames, newMemberName.trim()]
      }));
      setNewMemberName('');
    }
  };

  const handleRemoveMember = (index: number) => {
    setFormData(prev => ({
      ...prev,
      memberNames: prev.memberNames.filter((_, i) => i !== index)
    }));
  };

  const handleAddReviewer = () => {
    if (newReviewerName.trim()) {
      setFormData(prev => ({
        ...prev,
        peerReviewerNames: [...prev.peerReviewerNames, newReviewerName.trim()]
      }));
      setNewReviewerName('');
    }
  };

  const handleRemoveReviewer = (index: number) => {
    setFormData(prev => ({
      ...prev,
      peerReviewerNames: prev.peerReviewerNames.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Create team object with proper structure
      const teamData = {
        members: formData.memberNames.map((name, index) => `member-${index + 1}`),
        memberNames: formData.memberNames,
        peerReviewers: formData.peerReviewerNames.map((name, index) => `reviewer-${index + 1}`),
        peerReviewerNames: formData.peerReviewerNames
      };
      await onSave(teamData);
      onClose();
    } catch (error) {
      console.error('Error saving team:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        Edit Team
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Team Members Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              Team Members
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Add Team Member"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddMember();
                  }
                }}
                placeholder="Enter name and press Enter"
                aria-label="Add team member name"
              />
              <Button
                variant="contained"
                onClick={handleAddMember}
                sx={{ backgroundColor: 'primary.main', minWidth: '100px' }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {formData.memberNames.map((name, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {name.charAt(0).toUpperCase()}
                    </Box>
                    <Typography>{name}</Typography>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveMember(index)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              {formData.memberNames.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No team members yet. Add your first team member above.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Peer Reviewers Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: 'primary.main', mb: 2 }}>
              Peer Reviewers
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Add Peer Reviewer"
                value={newReviewerName}
                onChange={(e) => setNewReviewerName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddReviewer();
                  }
                }}
                placeholder="Enter name and press Enter"
                aria-label="Add peer reviewer name"
              />
              <Button
                variant="contained"
                onClick={handleAddReviewer}
                sx={{ backgroundColor: '#4caf50', minWidth: '100px' }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {formData.peerReviewerNames.map((name, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {name.charAt(0).toUpperCase()}
                    </Box>
                    <Typography>{name}</Typography>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveReviewer(index)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              {formData.peerReviewerNames.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No peer reviewers yet. Add your first peer reviewer above.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{ backgroundColor: 'primary.main' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
